import { useState, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { apiRequest } from "@/lib/queryClient";
import freshorderLogo from "../assets/freshorder-logo.png";
import { Loader2, Mail, CheckCircle2, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const [isVerifying, setIsVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext?.organization;
  const orgSlug = orgContext?.orgSlug;

  const logoSrc = organization?.logoUrl || freshorderLogo;
  const logoAlt = organization?.name || "FreshOrder";

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setIsVerifying(false);
        setError("No verification token provided");
        return;
      }

      try {
        const response = await apiRequest("POST", `/api/customer/verify-email`, { token });
        
        if (response.ok) {
          const data = await response.json();
          setVerified(true);
          setCustomUrl(data.customUrl);
          toast({
            title: "Email verified!",
            description: "Your email has been added to your account.",
          });
        } else {
          const errorData = await response.json();
          setError(errorData.message || "Verification failed");
        }
      } catch (err) {
        setError("Unable to verify email. Please try again.");
      }
      
      setIsVerifying(false);
    };

    verifyEmail();
  }, [token, toast]);

  const handleGoToOrder = () => {
    if (customUrl && orgSlug) {
      setLocation(`/org/${orgSlug}/order/${customUrl}`);
    } else if (orgSlug) {
      setLocation(`/org/${orgSlug}`);
    } else {
      setLocation("/");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border border-gray-200">
        <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
          <img 
            src={logoSrc} 
            alt={logoAlt} 
            className="h-14 mx-auto mb-4 object-contain"
            data-testid="img-org-logo"
          />
          <p className="text-sm text-gray-600">
            {isVerifying ? "Verifying your email..." : verified ? "Email Verified!" : "Verification"}
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {isVerifying && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-4 text-gray-600">Verifying your email address...</p>
            </div>
          )}

          {!isVerifying && error && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-gray-700 font-medium">Verification Failed</p>
              <p className="text-sm text-gray-500">{error}</p>
              <Button
                onClick={handleGoToOrder}
                className="mt-4"
                data-testid="button-back"
              >
                Go to Home
              </Button>
            </div>
          )}

          {!isVerifying && verified && (
            <div className="text-center py-8 space-y-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <p className="text-xl font-medium text-gray-900">Email Verified!</p>
              <p className="text-gray-600">
                Your email has been added to your account. You can now receive email notifications and order receipts.
              </p>
              <Button
                onClick={handleGoToOrder}
                className="mt-6 w-full bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                data-testid="button-continue"
              >
                <Mail className="h-4 w-4 mr-2" />
                Continue to Order
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
